import React from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Input,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  IconButton,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Home,
  Search as SearchIcon,
  History as HistoryIcon,
  Download,
  CheckCircle,
  Cancel,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContactPhone as ContactPhoneIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import OcorrenciasFornecedoresTab from '../components/OcorrenciasFornecedoresTab';

/* ─── Helpers de validação ─────────────────────────────── */
const onlyNumbers = (v) => (v || '').replace(/\D/g, '');

const formatPhone = (v) => {
  const nums = onlyNumbers(v);
  if (nums.length <= 2) return nums;
  if (nums.length <= 6) return `(${nums.slice(0, 2)}) ${nums.slice(2)}`;
  if (nums.length <= 10) return `(${nums.slice(0, 2)}) ${nums.slice(2, 6)}-${nums.slice(6)}`;
  return `(${nums.slice(0, 2)}) ${nums.slice(2, 7)}-${nums.slice(7, 11)}`;
};

const isValidCPF = (cpf) => {
  const nums = onlyNumbers(cpf);
  if (nums.length !== 11) return false;
  if (/^(\d)\1+$/.test(nums)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(nums[i]) * (10 - i);
  let rest = (sum * 10) % 11;
  if (rest === 10) rest = 0;
  if (rest !== parseInt(nums[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(nums[i]) * (11 - i);
  rest = (sum * 10) % 11;
  if (rest === 10) rest = 0;
  return rest === parseInt(nums[10]);
};

const isValidCNPJ = (cnpjValue) => {
  const cnpj = (cnpjValue || '').replace(/[^\dA-Za-z]/g, '').toUpperCase();
  if (cnpj.length !== 14) return false;
  if (/^([0-9A-Z])\1+$/.test(cnpj)) return false;

  const val = (c) => c.charCodeAt(0) - 48;
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let sum = 0;
  for (let i = 0; i < 12; i++) sum += val(cnpj[i]) * weights1[i];
  let rest = sum % 11;
  if (rest < 2) rest = 0;
  else rest = 11 - rest;
  if (rest !== parseInt(cnpj[12], 10)) return false;

  sum = 0;
  for (let i = 0; i < 13; i++) sum += (i === 12 ? rest : val(cnpj[i])) * weights2[i];
  rest = sum % 11;
  if (rest < 2) rest = 0;
  else rest = 11 - rest;
  return rest === parseInt(cnpj[13], 10);
};

const isValidCPForCNPJ = (value) => {
  const alphaNum = (value || '').replace(/[^\dA-Za-z]/g, '').toUpperCase();
  if (alphaNum.length === 11 && /^\d+$/.test(alphaNum)) return isValidCPF(alphaNum);
  if (alphaNum.length === 14) return isValidCNPJ(alphaNum);
  return false;
};

const formatCPFCNPJ = (value) => {
  const alphaNum = (value || '').replace(/[^\dA-Za-z]/g, '').toUpperCase();
  if (alphaNum.length <= 11 && /^\d*$/.test(alphaNum)) {
    const nums = alphaNum;
    if (nums.length <= 3) return nums;
    if (nums.length <= 6) return `${nums.slice(0, 3)}.${nums.slice(3)}`;
    if (nums.length <= 9) return `${nums.slice(0, 3)}.${nums.slice(3, 6)}.${nums.slice(6)}`;
    return `${nums.slice(0, 3)}.${nums.slice(3, 6)}.${nums.slice(6, 9)}-${nums.slice(9, 11)}`;
  }
  const nums = alphaNum;
  if (nums.length <= 2) return nums;
  if (nums.length <= 5) return `${nums.slice(0, 2)}.${nums.slice(2)}`;
  if (nums.length <= 8) return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5)}`;
  if (nums.length <= 12)
    return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8)}`;
  return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8, 12)}-${nums.slice(12, 14)}`;
};

const normalizeAtivo = (value) => {
  if (value === undefined || value === null || value === '') return true;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['false', '0', 'nao', 'não', 'inativo', 'inactive'].includes(normalized)) return false;
    if (['true', '1', 'sim', 'ativo', 'active'].includes(normalized)) return true;
  }
  return Boolean(value);
};

/* ─── Componente Principal ─────────────────────────────── */
export default function Fornecedores() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { token, hasPermission, isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : !!isAdmin;
  const canCreate = hasPermission('/fornecedores', 'create');
  const canUpdate = hasPermission('/fornecedores', 'update');

  /* ─── States ──────────────────────────────────────────── */
  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [selectedFornecedor, setSelectedFornecedor] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openModal, setOpenModal] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [editForm, setEditForm] = React.useState({});
  const [editFile, setEditFile] = React.useState(null);

  // Tabs principais
  const [mainTabIndex, setMainTabIndex] = React.useState(0);
  // Tab dentro do detalhe
  const [detailTabIndex, setDetailTabIndex] = React.useState(0);

  // Ocorrências por fornecedor
  const [ocorrencias, setOcorrencias] = React.useState([]);
  const [loadingOcorrencias, setLoadingOcorrencias] = React.useState(false);
  const [openOcorrenciaModal, setOpenOcorrenciaModal] = React.useState(false);
  const [ocorrenciaForm, setOcorrenciaForm] = React.useState({
    titulo: '',
    descricao: '',
    tipo: 'Informação',
    status: 'Aberta',
    prioridade: 'Normal',
  });

  // Filtros
  const [openFilter, setOpenFilter] = React.useState(false);
  const [filters, setFilters] = React.useState({
    searchText: '',
    orderBy: 'nome',
    orderDirection: 'asc',
    apenasAtivos: false,
    categoria: '',
  });

  // Upload situação cadastral
  const [selectedFile, setSelectedFile] = React.useState(null);

  // Contatos do fornecedor
  const [contatos, setContatos] = React.useState([]);
  const [loadingContatos, setLoadingContatos] = React.useState(false);
  const [contatoForm, setContatoForm] = React.useState({
    nome: '',
    cargo: '',
    telefone: '',
    email: '',
    principal: false,
  });
  const [editingContato, setEditingContato] = React.useState(null);
  const [openContatoModal, setOpenContatoModal] = React.useState(false);

  // Categorias de fornecedores
  const CATEGORIAS_FORNECEDOR = [
    'Aço',
    'Aluguel de Equipamentos',
    'Argamassa',
    'Cerâmica',
    'Concreto',
    'Elétrica',
    'Ferragens',
    'Frete',
    'Gesso',
    'Hidráulica',
    'Impermeabilização',
    'Madeira',
    'Mão de Obra',
    'Materiais de Construção',
    'Mármores e Granitos',
    'Paisagismo',
    'Pintura',
    'Pisos e Revestimentos',
    'Serralheria',
    'Terraplanagem',
    'Tintas',
    'Vidros',
    'Outros',
  ];

  // Form de criação
  const emptyForm = {
    nome: '',
    nome_fantasia: '',
    cnpj: '',
    cpf: '',
    telefone: '',
    email: '',
    cep: '',
    logradouro: '',
    numero_endereco: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    situacao_cadastral: '',
    ativo: true,
    observacao: '',
    categoria: '',
    responsavel_nome: '',
    responsavel_telefone: '',
  };
  const [form, setForm] = React.useState({ ...emptyForm });
  const [cpfCnpjError, setCpfCnpjError] = React.useState('');

  const API = '/api';
  const handleBack = () => navigate('/');

  /* ─── Helpers de payload (padrão Rails) ────────────── */
  const buildPayload = (raw) => ({
    nome: raw.nome,
    nome_fantasia: raw.nome_fantasia,
    cnpj: raw.cnpj,
    cpf: raw.cpf,
    telefone: onlyNumbers(raw.telefone),
    contato: onlyNumbers(raw.telefone),
    email: raw.email,
    cep: raw.cep,
    logradouro: raw.logradouro,
    numero_endereco: raw.numero_endereco,
    complemento: raw.complemento,
    bairro: raw.bairro,
    cidade: raw.cidade,
    estado: raw.estado,
    situacao_cadastral: raw.situacao_cadastral,
    ativo: normalizeAtivo(raw.ativo),
    observacao: raw.observacao,
    categoria: raw.categoria,
    responsavel_nome: raw.responsavel_nome,
    responsavel_telefone: onlyNumbers(raw.responsavel_telefone),
  });

  const normalizeRow = (r) => ({
    ...r,
    ativo: normalizeAtivo(r.ativo),
    telefone: r.telefone || r.contato || '',
  });

  /* ─── CRUD & Dados ────────────────────────────────────── */
  const loadPersisted = React.useCallback(async () => {
    try {
      const resp = await fetch(`${API}/fornecedores`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const normalized = (data || []).map(normalizeRow);
      setRows(normalized);
      setFilteredRows(normalized);
    } catch (e) {
      console.error('Erro ao carregar fornecedores:', e);
    }
  }, [API, token]);

  const loadOcorrencias = React.useCallback(
    async (fornecedorId) => {
      if (!fornecedorId) return;
      setLoadingOcorrencias(true);
      try {
        const resp = await fetch(`${API}/fornecedores/${fornecedorId}/ocorrencias`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (resp.ok) {
          setOcorrencias(await resp.json());
        }
      } catch (e) {
        console.error('Erro ao carregar ocorrências:', e);
      } finally {
        setLoadingOcorrencias(false);
      }
    },
    [API, token]
  );

  const loadContatos = React.useCallback(
    async (fornecedorId) => {
      if (!fornecedorId) return;
      setLoadingContatos(true);
      try {
        const resp = await fetch(`${API}/fornecedores/${fornecedorId}/contatos`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (resp.ok) {
          setContatos(await resp.json());
        }
      } catch (e) {
        console.error('Erro ao carregar contatos:', e);
      } finally {
        setLoadingContatos(false);
      }
    },
    [API, token]
  );

  /* ─── Filtros ─────────────────────────────────────────── */
  const applyFilters = React.useCallback(() => {
    let filtered = [...rows];
    if (filters.searchText) {
      const s = filters.searchText.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          (r.nome || '').toLowerCase().includes(s) ||
          (r.nome_fantasia || '').toLowerCase().includes(s) ||
          (r.cnpj || '').toLowerCase().includes(s) ||
          (r.cpf || '').toLowerCase().includes(s) ||
          (r.telefone || '').toLowerCase().includes(s) ||
          (r.cidade || '').toLowerCase().includes(s) ||
          (r.categoria || '').toLowerCase().includes(s)
      );
    }
    if (filters.categoria) {
      filtered = filtered.filter((r) => (r.categoria || '') === filters.categoria);
    }
    if (filters.apenasAtivos) {
      filtered = filtered.filter((r) => normalizeAtivo(r.ativo));
    }
    filtered.sort((a, b) => {
      const aVal = (a[filters.orderBy] || '').toString().toLowerCase();
      const bVal = (b[filters.orderBy] || '').toString().toLowerCase();
      return filters.orderDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    setFilteredRows(filtered);
    setPage(0);
  }, [filters, rows]);

  React.useEffect(() => {
    applyFilters();
  }, [applyFilters]);
  React.useEffect(() => {
    loadPersisted();
  }, [loadPersisted]);

  /* ─── Import/Export removido da UI de Fornecedores ─────── */

  /* ─── Formatar CEP ───────────────────────────────────── */
  const formatCEP = (v) => {
    const nums = onlyNumbers(v).slice(0, 8);
    if (nums.length <= 5) return nums;
    return `${nums.slice(0, 5)}-${nums.slice(5)}`;
  };

  /* ─── Busca CEP ───────────────────────────────────────── */
  const buscarCEP = async (cep, setter) => {
    const nums = onlyNumbers(cep);
    if (nums.length !== 8) return;
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${nums}/json/`);
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.erro) return;
      setter((f) => ({
        ...f,
        logradouro: data.logradouro || f.logradouro,
        bairro: data.bairro || f.bairro,
        cidade: data.localidade || f.cidade,
        estado: data.uf || f.estado,
        complemento: data.complemento || f.complemento,
      }));
    } catch {}
  };

  /* ─── Validação CPF/CNPJ on blur ─────────────────────── */
  const handleCpfCnpjBlur = (value) => {
    if (!value) {
      setCpfCnpjError('');
      return;
    }
    const alphaNum = (value || '').replace(/[^\dA-Za-z]/g, '');
    if (alphaNum.length === 0) {
      setCpfCnpjError('');
      return;
    }
    if (alphaNum.length !== 11 && alphaNum.length !== 14) {
      setCpfCnpjError('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido');
      return;
    }
    if (!isValidCPForCNPJ(value)) {
      setCpfCnpjError(alphaNum.length === 11 ? 'CPF inválido' : 'CNPJ inválido');
      return;
    }
    setCpfCnpjError('');
  };

  /* ─── Upload situação cadastral ────────────────────────── */
  const handleUploadSituacaoCadastral = async (fornecedorId, file) => {
    if (!file || !fornecedorId) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await fetch(`${API}/fornecedores/${fornecedorId}/upload_situacao_cadastral`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!resp.ok) throw new Error('Erro no upload');
      const result = await resp.json();
      await loadPersisted();
      return result;
    } catch (e) {
      alert('Erro ao enviar arquivo de situação cadastral');
      return null;
    }
  };

  const handleDownloadSituacaoCadastral = async (fornecedorId, filename) => {
    try {
      const resp = await fetch(`${API}/fornecedores/${fornecedorId}/download_situacao_cadastral`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `situacao_cadastral_${fornecedorId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      alert('Falha ao baixar arquivo');
    }
  };

  /* ─── Criar fornecedor ─────────────────────────────────── */
  const handleCreate = async () => {
    if (!form.nome) {
      alert('Nome é obrigatório');
      return;
    }
    const cpfCnpjVal = form.cnpj || form.cpf;
    if (cpfCnpjVal && !isValidCPForCNPJ(cpfCnpjVal)) {
      alert('CPF ou CNPJ inválido');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(form);
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(`fornecedor[${k}]`, v);
      });

      const resp = await fetch(`${API}/fornecedores`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!resp.ok) {
        let msg = 'Erro ao criar fornecedor';
        try {
          const d = await resp.json();
          msg = d.detail || d.errors?.join(', ') || msg;
        } catch {}
        throw new Error(msg);
      }
      const created = await resp.json();
      // Se tiver arquivo de situação cadastral, faz upload separado
      if (selectedFile && created.id) {
        await handleUploadSituacaoCadastral(created.id, selectedFile);
      }
      await loadPersisted();
      setForm({ ...emptyForm });
      setSelectedFile(null);
      setOpenModal(false);
    } catch (e) {
      alert(e.message || 'Erro ao criar fornecedor');
    } finally {
      setSaving(false);
    }
  };

  /* ─── Atualizar fornecedor ─────────────────────────────── */
  const handleUpdate = async () => {
    if (!editForm.nome) {
      alert('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(editForm);
      let resp;
      if (editFile) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') fd.append(`fornecedor[${k}]`, v);
        });
        resp = await fetch(`${API}/fornecedores/${selectedFornecedor.id}`, {
          method: 'PUT',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: fd,
        });
      } else {
        resp = await fetch(`${API}/fornecedores/${selectedFornecedor.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ fornecedor: payload }),
        });
      }
      if (!resp.ok) {
        let msg = 'Erro ao atualizar fornecedor';
        try {
          const d = await resp.json();
          msg = d.detail || d.errors?.join(', ') || msg;
        } catch {}
        throw new Error(msg);
      }
      if (editFile && selectedFornecedor.id) {
        await handleUploadSituacaoCadastral(selectedFornecedor.id, editFile);
      }
      await loadPersisted();
      setSelectedFornecedor(null);
      setEditMode(false);
      setEditForm({});
      setEditFile(null);
    } catch (e) {
      alert(e.message || 'Erro ao atualizar fornecedor');
    } finally {
      setSaving(false);
    }
  };

  /* ─── Excluir fornecedor ───────────────────────────────── */
  const handleDelete = async () => {
    if (!selectedFornecedor?.id) return;
    if (!window.confirm('Deseja realmente excluir este fornecedor?')) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API}/fornecedores/${selectedFornecedor.id}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (resp.ok || resp.status === 204) {
        await loadPersisted();
        setSelectedFornecedor(null);
        setEditMode(false);
      } else {
        alert('Erro ao excluir fornecedor');
      }
    } catch {
      alert('Erro ao excluir fornecedor');
    } finally {
      setSaving(false);
    }
  };

  /* ─── Criar ocorrência ─────────────────────────────────── */
  const handleCreateOcorrencia = async () => {
    if (!ocorrenciaForm.titulo) {
      alert('Título é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const resp = await fetch(`${API}/fornecedores/${selectedFornecedor.id}/criar_ocorrencia`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ocorrencia: ocorrenciaForm }),
      });
      if (!resp.ok) throw new Error('Erro ao criar ocorrência');
      await loadOcorrencias(selectedFornecedor.id);
      setOpenOcorrenciaModal(false);
      setOcorrenciaForm({
        titulo: '',
        descricao: '',
        tipo: 'Informação',
        status: 'Aberta',
        prioridade: 'Normal',
      });
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOcorrencia = async (ocId) => {
    if (!window.confirm('Excluir esta ocorrência?')) return;
    try {
      await fetch(`${API}/fornecedores/${selectedFornecedor.id}/ocorrencias/${ocId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      await loadOcorrencias(selectedFornecedor.id);
    } catch {
      alert('Erro ao excluir ocorrência');
    }
  };

  /* ─── CRUD Contatos ─────────────────────────────────── */
  const handleCreateContato = async () => {
    if (!contatoForm.nome) {
      alert('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const resp = await fetch(`${API}/fornecedores/${selectedFornecedor.id}/criar_contato`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          contato: { ...contatoForm, telefone: onlyNumbers(contatoForm.telefone) },
        }),
      });
      if (!resp.ok) throw new Error('Erro ao criar contato');
      await loadContatos(selectedFornecedor.id);
      setOpenContatoModal(false);
      setContatoForm({ nome: '', cargo: '', telefone: '', email: '', principal: false });
      setEditingContato(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateContato = async () => {
    if (!contatoForm.nome) {
      alert('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const resp = await fetch(
        `${API}/fornecedores/${selectedFornecedor.id}/contatos/${editingContato.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            contato: { ...contatoForm, telefone: onlyNumbers(contatoForm.telefone) },
          }),
        }
      );
      if (!resp.ok) throw new Error('Erro ao atualizar contato');
      await loadContatos(selectedFornecedor.id);
      setOpenContatoModal(false);
      setContatoForm({ nome: '', cargo: '', telefone: '', email: '', principal: false });
      setEditingContato(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContato = async (contatoId) => {
    if (!window.confirm('Excluir este contato?')) return;
    try {
      await fetch(`${API}/fornecedores/${selectedFornecedor.id}/contatos/${contatoId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      await loadContatos(selectedFornecedor.id);
    } catch {
      alert('Erro ao excluir contato');
    }
  };

  /* ─── Formatação data/hora ─────────────────────────────── */
  const formatDataHora = (dt) => {
    if (!dt) return '';
    try {
      const d = new Date(dt);
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dt;
    }
  };

  /* ─── Campos de endereço compartilhados ─────────────── */
  const renderEnderecoFields = (formState, setter, isEditing = true) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        📍 Endereço
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 180px', minWidth: { xs: '100%', sm: 180 } }}>
          <TextField
            label="CEP"
            fullWidth
            size="small"
            value={formState.cep || ''}
            onChange={(e) => {
              const masked = formatCEP(e.target.value);
              setter((f) => ({ ...f, cep: masked }));
              if (onlyNumbers(masked).length === 8) buscarCEP(masked, setter);
            }}
            onBlur={(e) => buscarCEP(e.target.value, setter)}
            disabled={!isEditing}
            placeholder="00000-000"
            inputProps={{ maxLength: 9 }}
          />
        </Box>
        <Box sx={{ flex: '2 1 300px', minWidth: { xs: '100%', sm: 300 } }}>
          <TextField
            label="Logradouro"
            fullWidth
            size="small"
            value={formState.logradouro || ''}
            onChange={(e) => setter((f) => ({ ...f, logradouro: e.target.value }))}
            disabled={!isEditing}
          />
        </Box>
        <Box sx={{ flex: '1 1 120px', minWidth: { xs: '45%', sm: 120 } }}>
          <TextField
            label="Número"
            fullWidth
            size="small"
            value={formState.numero_endereco || ''}
            onChange={(e) => setter((f) => ({ ...f, numero_endereco: e.target.value }))}
            disabled={!isEditing}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: { xs: '45%', sm: 180 } }}>
          <TextField
            label="Complemento"
            fullWidth
            size="small"
            value={formState.complemento || ''}
            onChange={(e) => setter((f) => ({ ...f, complemento: e.target.value }))}
            disabled={!isEditing}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: { xs: '100%', sm: 180 } }}>
          <TextField
            label="Bairro"
            fullWidth
            size="small"
            value={formState.bairro || ''}
            onChange={(e) => setter((f) => ({ ...f, bairro: e.target.value }))}
            disabled={!isEditing}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: { xs: '60%', sm: 180 } }}>
          <TextField
            label="Cidade"
            fullWidth
            size="small"
            value={formState.cidade || ''}
            onChange={(e) => setter((f) => ({ ...f, cidade: e.target.value }))}
            disabled={!isEditing}
          />
        </Box>
        <Box sx={{ flex: '0 1 90px', minWidth: { xs: '30%', sm: 90 } }}>
          <FormControl fullWidth size="small">
            <InputLabel>UF</InputLabel>
            <Select
              value={formState.estado || ''}
              label="UF"
              onChange={(e) => setter((f) => ({ ...f, estado: e.target.value }))}
              disabled={!isEditing}
            >
              {[
                'AC',
                'AL',
                'AM',
                'AP',
                'BA',
                'CE',
                'DF',
                'ES',
                'GO',
                'MA',
                'MG',
                'MS',
                'MT',
                'PA',
                'PB',
                'PE',
                'PI',
                'PR',
                'RJ',
                'RN',
                'RO',
                'RR',
                'RS',
                'SC',
                'SE',
                'SP',
                'TO',
              ].map((uf) => (
                <MenuItem key={uf} value={uf}>
                  {uf}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );

  /* ─── Campos de situação cadastral compartilhados ────── */
  const renderSituacaoCadastralFields = (
    formState,
    setter,
    fileState,
    fileSetter,
    isEditing = true,
    fornecedorData = null
  ) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        📄 Situação Cadastral
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
          <FormControl fullWidth>
            <InputLabel>Situação</InputLabel>
            <Select
              value={formState.situacao_cadastral || ''}
              label="Situação"
              onChange={(e) => setter((f) => ({ ...f, situacao_cadastral: e.target.value }))}
              disabled={!isEditing}
            >
              <MenuItem value="">Nenhuma</MenuItem>
              <MenuItem value="Regular">Regular</MenuItem>
              <MenuItem value="Irregular">Irregular</MenuItem>
              <MenuItem value="Suspensa">Suspensa</MenuItem>
              <MenuItem value="Inapta">Inapta</MenuItem>
              <MenuItem value="Baixada">Baixada</MenuItem>
              <MenuItem value="Nula">Nula</MenuItem>
              <MenuItem value="Ativa">Ativa</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
          {isEditing && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block' }}
              >
                Anexar foto/PDF da situação cadastral
              </Typography>
              <Input
                type="file"
                inputProps={{ accept: 'image/*,application/pdf' }}
                onChange={(e) => fileSetter(e.target.files[0])}
                fullWidth
              />
            </Box>
          )}
          {fornecedorData?.situacao_cadastral_arquivo && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                onClick={() =>
                  handleDownloadSituacaoCadastral(
                    fornecedorData.id,
                    fornecedorData.situacao_cadastral_arquivo
                  )
                }
              >
                Baixar arquivo
              </Button>
              {fornecedorData.situacao_cadastral_data_envio && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  📅 Enviado em: {formatDataHora(fornecedorData.situacao_cadastral_data_envio)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  /* ═══════════════════════════════════════════════════════ */
  /* ─── RENDER ──────────────────────────────────────────── */
  /* ═══════════════════════════════════════════════════════ */
  return (
    <Box
      sx={{
        background: theme.palette.background.default,
        minHeight: '100vh',
        p: { xs: 1, sm: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1600, mx: 'auto', mt: 3 }}>
        {/* ─── Header / Breadcrumb ─── */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton onClick={handleBack} size="small" aria-label="Voltar ao Dashboard">
            <ArrowBack />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ ml: 1, '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
            <Link
              underline="hover"
              color="inherit"
              onClick={handleBack}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <Home sx={{ mr: 0.5 }} fontSize="inherit" /> Dashboard
            </Link>
            <Typography color="text.primary">Fornecedores</Typography>
          </Breadcrumbs>
        </Box>

        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 0, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}
        >
          Gestão de Fornecedores
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Gerencie seus fornecedores de forma eficiente
        </Typography>

        {/* ─── Tabs Globais (admin vê Ocorrências) ─── */}
        {isAdminUser && (
          <Tabs value={mainTabIndex} onChange={(_, v) => setMainTabIndex(v)} sx={{ mb: 3 }}>
            <Tab label="Fornecedores" icon={<SearchIcon />} iconPosition="start" />
            <Tab label="Ocorrências" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        )}

        {/* ═══════ TAB 0: Lista de Fornecedores ═══════ */}
        {mainTabIndex === 0 && (
          <>
            {/* Botões de ação */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3,
                alignItems: { xs: 'stretch', sm: 'center' },
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1 }}>
                {canCreate && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      setForm({ ...emptyForm });
                      setSelectedFile(null);
                      setCpfCnpjError('');
                      setOpenModal(true);
                    }}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{ fontWeight: 600 }}
                  >
                    Novo Fornecedor
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={() => setOpenFilter(true)}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Filtros
                </Button>
              </Stack>
            </Box>

            {/* Chips de filtro ativos */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              {filters.searchText && (
                <Chip
                  label={`Busca: ${filters.searchText}`}
                  onDelete={() => setFilters((f) => ({ ...f, searchText: '' }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              )}
              {filters.categoria && (
                <Chip
                  label={`Categoria: ${filters.categoria}`}
                  onDelete={() => setFilters((f) => ({ ...f, categoria: '' }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              )}
              {filters.apenasAtivos && (
                <Chip
                  label="Apenas Ativos"
                  onDelete={() => setFilters((f) => ({ ...f, apenasAtivos: false }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              )}
            </Stack>

            {/* ─── Tabela Desktop ─── */}
            {!isMobile ? (
              <TableContainer
                component={Paper}
                sx={{ borderRadius: 2, boxShadow: 0, overflowX: 'hidden' }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ background: '#f4f6f8' }}>
                      <TableCell sx={{ fontWeight: 700, width: '16%' }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '15%' }}>Nome Fantasia</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '11%', whiteSpace: 'nowrap' }}>
                        CPF/CNPJ
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '10%', whiteSpace: 'nowrap' }}>
                        Telefone
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '10%' }}>Categoria</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '10%' }}>Cidade/UF</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '8%' }}>Situação</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '8%' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '12%', whiteSpace: 'nowrap' }}>
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, idx) => {
                        const isInativo = !normalizeAtivo(row.ativo);
                        return (
                          <TableRow
                            key={row.id || idx}
                            hover
                            sx={{
                              borderBottom: '1px solid #e0e0e0',
                              backgroundColor: isInativo ? '#fde2e4' : 'transparent',
                              '& > td:first-of-type': {
                                borderLeft: isInativo ? '6px solid #f48fb1' : 'none',
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.nome || ''}
                            </TableCell>
                            <TableCell
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.nome_fantasia || ''}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {row.cnpj || row.cpf || ''}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {row.telefone ? formatPhone(row.telefone) : ''}
                            </TableCell>
                            <TableCell>
                              {row.categoria ? (
                                <Chip label={row.categoria} size="small" variant="outlined" />
                              ) : (
                                ''
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {[row.cidade, row.estado].filter(Boolean).join('/') || ''}
                            </TableCell>
                            <TableCell
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.situacao_cadastral || ''}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip
                                icon={normalizeAtivo(row.ativo) ? <CheckCircle /> : <Cancel />}
                                label={normalizeAtivo(row.ativo) ? 'Ativo' : 'Inativo'}
                                color={normalizeAtivo(row.ativo) ? 'success' : 'error'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  setSelectedFornecedor(row);
                                  setEditMode(false);
                                  setDetailTabIndex(0);
                                  loadOcorrencias(row.id);
                                  loadContatos(row.id);
                                }}
                              >
                                Detalhes
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredRows.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Linhas por página:"
                />
              </TableContainer>
            ) : (
              /* ─── Cards Mobile ─── */
              <Box>
                {filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, idx) => {
                    const isInativo = !normalizeAtivo(row.ativo);
                    return (
                      <Card
                        key={row.id || idx}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          backgroundColor: isInativo ? '#fde2e4' : 'background.paper',
                          borderLeft: isInativo ? '6px solid #f48fb1' : 'none',
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              mb: 1,
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {row.nome || ''}
                            </Typography>
                            <Chip
                              label={normalizeAtivo(row.ativo) ? 'Ativo' : 'Inativo'}
                              color={normalizeAtivo(row.ativo) ? 'success' : 'error'}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          {row.nome_fantasia && (
                            <Typography variant="body2" color="text.secondary">
                              Fantasia: {row.nome_fantasia}
                            </Typography>
                          )}
                          {(row.cnpj || row.cpf) && (
                            <Typography variant="body2" color="text.secondary">
                              CPF/CNPJ: {row.cnpj || row.cpf}
                            </Typography>
                          )}
                          {row.telefone && (
                            <Typography variant="body2" color="text.secondary">
                              Tel: {formatPhone(row.telefone)}
                            </Typography>
                          )}
                          {row.categoria && (
                            <Chip
                              label={row.categoria}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                          {(row.cidade || row.estado) && (
                            <Typography variant="body2" color="text.secondary">
                              {[row.cidade, row.estado].filter(Boolean).join('/')}
                            </Typography>
                          )}
                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            sx={{ mt: 2 }}
                            onClick={() => {
                              setSelectedFornecedor(row);
                              setEditMode(false);
                              setDetailTabIndex(0);
                              loadOcorrencias(row.id);
                              loadContatos(row.id);
                            }}
                          >
                            Ver detalhes
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={filteredRows.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Por página:"
                  />
                </Box>
              </Box>
            )}

            {/* Status */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
              {filteredRows.length === 0
                ? 'Nenhum fornecedor encontrado'
                : `${filteredRows.length} fornecedores encontrados`}
            </Typography>
          </>
        )}

        {/* ═══════ TAB 1: Ocorrências Globais (admin) ═══════ */}
        {mainTabIndex === 1 && isAdminUser && <OcorrenciasFornecedoresTab token={token} />}

        {/* ═══════════════════════════════════════════════════ */}
        {/* ─── Modal de Filtros ──────────────────────────── */}
        <Dialog open={openFilter} onClose={() => setOpenFilter(false)} maxWidth="md" fullWidth>
          <DialogTitle>Filtros e Ordenação</DialogTitle>
          <DialogContent sx={{ minWidth: isMobile ? 300 : 500 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: 2,
                mt: 1,
              }}
            >
              <TextField
                label="Buscar por nome, nome fantasia, CPF/CNPJ, telefone ou cidade"
                fullWidth
                value={filters.searchText}
                onChange={(e) => setFilters((f) => ({ ...f, searchText: e.target.value }))}
                placeholder="Digite para buscar..."
              />
              <FormControl fullWidth>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={filters.orderBy}
                  onChange={(e) => setFilters((f) => ({ ...f, orderBy: e.target.value }))}
                  label="Ordenar por"
                >
                  <MenuItem value="nome">Nome (A-Z)</MenuItem>
                  <MenuItem value="cnpj">CPF/CNPJ</MenuItem>
                  <MenuItem value="cidade">Cidade</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Direção</InputLabel>
                <Select
                  value={filters.orderDirection}
                  onChange={(e) => setFilters((f) => ({ ...f, orderDirection: e.target.value }))}
                  label="Direção"
                >
                  <MenuItem value="asc">Crescente (A-Z)</MenuItem>
                  <MenuItem value="desc">Decrescente (Z-A)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={filters.categoria}
                  onChange={(e) => setFilters((f) => ({ ...f, categoria: e.target.value }))}
                  label="Categoria"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {CATEGORIAS_FORNECEDOR.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.apenasAtivos}
                    onChange={(e) => setFilters((f) => ({ ...f, apenasAtivos: e.target.checked }))}
                  />
                }
                label="Apenas fornecedores ativos"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setFilters({
                  searchText: '',
                  orderBy: 'nome',
                  orderDirection: 'asc',
                  apenasAtivos: false,
                  categoria: '',
                })
              }
            >
              Limpar Filtros
            </Button>
            <Button onClick={() => setOpenFilter(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════ */}
        {/* ─── Modal de Cadastro ─────────────────────────── */}
        <Dialog
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setForm({ ...emptyForm });
            setSelectedFile(null);
            setCpfCnpjError('');
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Novo Fornecedor</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="Nome do Fornecedor *"
                  fullWidth
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="Nome Fantasia"
                  fullWidth
                  value={form.nome_fantasia}
                  onChange={(e) => setForm((f) => ({ ...f, nome_fantasia: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="CPF ou CNPJ"
                  fullWidth
                  value={form.cnpj}
                  onChange={(e) => setForm((f) => ({ ...f, cnpj: formatCPFCNPJ(e.target.value) }))}
                  onBlur={(e) => handleCpfCnpjBlur(e.target.value)}
                  error={!!cpfCnpjError}
                  helperText={cpfCnpjError || 'Aceita CPF (11 dígitos) ou CNPJ (14 dígitos)'}
                  inputProps={{ maxLength: 18 }}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="Telefone"
                  fullWidth
                  value={form.telefone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, telefone: formatPhone(e.target.value) }))
                  }
                  helperText="Apenas números"
                  inputProps={{ maxLength: 15 }}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="E-mail"
                  fullWidth
                  value={form.email}
                  type="email"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <FormControl fullWidth>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={form.categoria}
                    label="Categoria"
                    onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))}
                  >
                    <MenuItem value="">Nenhuma</MenuItem>
                    {CATEGORIAS_FORNECEDOR.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  flex: '0 1 120px',
                  minWidth: { xs: '100%', sm: 120 },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.ativo}
                      onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
                    />
                  }
                  label={form.ativo ? 'Ativo' : 'Inativo'}
                />
              </Box>
            </Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mt: 2, mb: 1, color: 'primary.main' }}
            >
              👤 Responsável
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="Nome do Responsável"
                  fullWidth
                  value={form.responsavel_nome || ''}
                  onChange={(e) => setForm((f) => ({ ...f, responsavel_nome: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="Telefone do Responsável"
                  fullWidth
                  value={form.responsavel_telefone || ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, responsavel_telefone: formatPhone(e.target.value) }))
                  }
                  inputProps={{ maxLength: 15 }}
                />
              </Box>
            </Box>
            {renderEnderecoFields(form, setForm, true)}
            {renderSituacaoCadastralFields(
              form,
              setForm,
              selectedFile,
              setSelectedFile,
              true,
              null
            )}
            <TextField
              label="Observação"
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 2 }}
              value={form.observacao}
              onChange={(e) => setForm((f) => ({ ...f, observacao: e.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenModal(false);
                setForm({ ...emptyForm });
                setSelectedFile(null);
                setCpfCnpjError('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={saving || !canCreate || !form.nome}
            >
              {saving ? 'Salvando...' : 'Criar fornecedor'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════ */}
        {/* ─── Modal de Detalhes / Edição / Ocorrências ──── */}
        <Dialog
          open={!!selectedFornecedor}
          onClose={() => {
            setSelectedFornecedor(null);
            setEditMode(false);
            setEditForm({});
            setEditFile(null);
            setDetailTabIndex(0);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{editMode ? 'Editar Fornecedor' : 'Detalhes do Fornecedor'}</DialogTitle>
          <DialogContent>
            {selectedFornecedor && (
              <Box sx={{ mt: 1 }}>
                {/* Tabs internas: Dados | Contatos | Ocorrências */}
                <Tabs
                  value={detailTabIndex}
                  onChange={(_, v) => setDetailTabIndex(v)}
                  sx={{ mb: 2 }}
                >
                  <Tab label="Dados" />
                  <Tab
                    label={`Contatos (${contatos.length})`}
                    icon={<ContactPhoneIcon />}
                    iconPosition="start"
                  />
                  <Tab label={`Ocorrências (${ocorrencias.length})`} />
                </Tabs>

                {/* ─── Tab Dados ─── */}
                {detailTabIndex === 0 && (
                  <>
                    {!editMode ? (
                      /* ─── Visualização ─── */
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography>
                              <b>Nome:</b> {selectedFornecedor.nome}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography>
                              <b>Nome Fantasia:</b> {selectedFornecedor.nome_fantasia || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography>
                              <b>CPF/CNPJ:</b>{' '}
                              {selectedFornecedor.cnpj || selectedFornecedor.cpf || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography>
                              <b>Telefone:</b>{' '}
                              {selectedFornecedor.telefone
                                ? formatPhone(selectedFornecedor.telefone)
                                : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography>
                              <b>E-mail:</b> {selectedFornecedor.email || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography>
                              <b>Categoria:</b>{' '}
                              {selectedFornecedor.categoria ? (
                                <Chip
                                  label={selectedFornecedor.categoria}
                                  size="small"
                                  variant="outlined"
                                />
                              ) : (
                                '-'
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography>
                              <b>Status:</b>{' '}
                              <Chip
                                label={
                                  normalizeAtivo(selectedFornecedor.ativo) ? 'Ativo' : 'Inativo'
                                }
                                color={
                                  normalizeAtivo(selectedFornecedor.ativo) ? 'success' : 'error'
                                }
                                size="small"
                              />
                            </Typography>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
                        >
                          📍 Endereço
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2">
                              <b>CEP:</b> {selectedFornecedor.cep || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={9}>
                            <Typography variant="body2">
                              <b>Logradouro:</b> {selectedFornecedor.logradouro || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="body2">
                              <b>Nº:</b> {selectedFornecedor.numero_endereco || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="body2">
                              <b>Complemento:</b> {selectedFornecedor.complemento || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2">
                              <b>Bairro:</b> {selectedFornecedor.bairro || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={8} sm={2}>
                            <Typography variant="body2">
                              <b>Cidade:</b> {selectedFornecedor.cidade || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={1}>
                            <Typography variant="body2">
                              <b>UF:</b> {selectedFornecedor.estado || '-'}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
                        >
                          📄 Situação Cadastral
                        </Typography>
                        <Typography variant="body2">
                          <b>Situação:</b> {selectedFornecedor.situacao_cadastral || '-'}
                        </Typography>
                        {selectedFornecedor.situacao_cadastral_arquivo && (
                          <Box sx={{ mt: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Download />}
                              onClick={() =>
                                handleDownloadSituacaoCadastral(
                                  selectedFornecedor.id,
                                  selectedFornecedor.situacao_cadastral_arquivo
                                )
                              }
                            >
                              Baixar arquivo ({selectedFornecedor.situacao_cadastral_arquivo})
                            </Button>
                            {selectedFornecedor.situacao_cadastral_data_envio && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 0.5 }}
                              >
                                📅 Enviado em:{' '}
                                {formatDataHora(selectedFornecedor.situacao_cadastral_data_envio)}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {selectedFornecedor.observacao && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2">
                              <b>Observação:</b> {selectedFornecedor.observacao}
                            </Typography>
                          </>
                        )}

                        {(selectedFornecedor.responsavel_nome ||
                          selectedFornecedor.responsavel_telefone) && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
                            >
                              👤 Responsável
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                  <b>Nome:</b> {selectedFornecedor.responsavel_nome || '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                  <b>Telefone:</b>{' '}
                                  {selectedFornecedor.responsavel_telefone
                                    ? formatPhone(selectedFornecedor.responsavel_telefone)
                                    : '-'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Box>
                    ) : (
                      /* ─── Edição ─── */
                      <Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                            <TextField
                              label="Nome *"
                              fullWidth
                              value={editForm.nome || ''}
                              onChange={(e) => setEditForm((f) => ({ ...f, nome: e.target.value }))}
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                            <TextField
                              label="Nome Fantasia"
                              fullWidth
                              value={editForm.nome_fantasia || ''}
                              onChange={(e) =>
                                setEditForm((f) => ({ ...f, nome_fantasia: e.target.value }))
                              }
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                            <TextField
                              label="CPF ou CNPJ"
                              fullWidth
                              value={editForm.cnpj || ''}
                              onChange={(e) =>
                                setEditForm((f) => ({ ...f, cnpj: formatCPFCNPJ(e.target.value) }))
                              }
                              onBlur={(e) => handleCpfCnpjBlur(e.target.value)}
                              error={!!cpfCnpjError}
                              helperText={cpfCnpjError}
                              inputProps={{ maxLength: 18 }}
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                            <TextField
                              label="Telefone"
                              fullWidth
                              value={editForm.telefone || ''}
                              onChange={(e) =>
                                setEditForm((f) => ({
                                  ...f,
                                  telefone: formatPhone(e.target.value),
                                }))
                              }
                              helperText="Apenas números"
                              inputProps={{ maxLength: 15 }}
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                            <TextField
                              label="E-mail"
                              fullWidth
                              value={editForm.email || ''}
                              type="email"
                              onChange={(e) =>
                                setEditForm((f) => ({ ...f, email: e.target.value }))
                              }
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                            <FormControl fullWidth>
                              <InputLabel>Categoria</InputLabel>
                              <Select
                                value={editForm.categoria || ''}
                                label="Categoria"
                                onChange={(e) =>
                                  setEditForm((f) => ({ ...f, categoria: e.target.value }))
                                }
                              >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {CATEGORIAS_FORNECEDOR.map((cat) => (
                                  <MenuItem key={cat} value={cat}>
                                    {cat}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          <Box
                            sx={{
                              flex: '0 1 120px',
                              minWidth: { xs: '100%', sm: 120 },
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={normalizeAtivo(editForm.ativo)}
                                  onChange={(e) =>
                                    setEditForm((f) => ({ ...f, ativo: e.target.checked }))
                                  }
                                />
                              }
                              label={normalizeAtivo(editForm.ativo) ? 'Ativo' : 'Inativo'}
                            />
                          </Box>
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mt: 2, mb: 1, color: 'primary.main' }}
                        >
                          👤 Responsável
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                            <TextField
                              label="Nome do Responsável"
                              fullWidth
                              value={editForm.responsavel_nome || ''}
                              onChange={(e) =>
                                setEditForm((f) => ({ ...f, responsavel_nome: e.target.value }))
                              }
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                            <TextField
                              label="Telefone do Responsável"
                              fullWidth
                              value={editForm.responsavel_telefone || ''}
                              onChange={(e) =>
                                setEditForm((f) => ({
                                  ...f,
                                  responsavel_telefone: formatPhone(e.target.value),
                                }))
                              }
                              inputProps={{ maxLength: 15 }}
                            />
                          </Box>
                        </Box>
                        {renderEnderecoFields(editForm, setEditForm, true)}
                        {renderSituacaoCadastralFields(
                          editForm,
                          setEditForm,
                          editFile,
                          setEditFile,
                          true,
                          selectedFornecedor
                        )}
                        <TextField
                          label="Observação"
                          fullWidth
                          multiline
                          rows={2}
                          sx={{ mt: 2 }}
                          value={editForm.observacao || ''}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, observacao: e.target.value }))
                          }
                        />
                      </Box>
                    )}
                  </>
                )}

                {/* ─── Tab Contatos ─── */}
                {detailTabIndex === 1 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Contatos do Fornecedor
                      </Typography>
                      {(isAdminUser || canCreate) && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setEditingContato(null);
                            setContatoForm({
                              nome: '',
                              cargo: '',
                              telefone: '',
                              email: '',
                              principal: false,
                            });
                            setOpenContatoModal(true);
                          }}
                        >
                          Novo Contato
                        </Button>
                      )}
                    </Box>

                    {loadingContatos ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : contatos.length === 0 ? (
                      <Alert severity="info">Nenhum contato registrado para este fornecedor.</Alert>
                    ) : (
                      <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Cargo</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Telefone</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Principal</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Ações</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contatos.map((ct) => (
                              <TableRow key={ct.id} hover>
                                <TableCell>{ct.nome}</TableCell>
                                <TableCell>{ct.cargo || '-'}</TableCell>
                                <TableCell>
                                  {ct.telefone ? formatPhone(ct.telefone) : '-'}
                                </TableCell>
                                <TableCell>{ct.email || '-'}</TableCell>
                                <TableCell>
                                  {ct.principal ? (
                                    <Chip label="Sim" size="small" color="primary" />
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingContato(ct);
                                      setContatoForm({
                                        nome: ct.nome,
                                        cargo: ct.cargo || '',
                                        telefone: ct.telefone ? formatPhone(ct.telefone) : '',
                                        email: ct.email || '',
                                        principal: !!ct.principal,
                                      });
                                      setOpenContatoModal(true);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  {isAdminUser && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteContato(ct.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* ─── Tab Ocorrências ─── */}
                {detailTabIndex === 2 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Ocorrências do Fornecedor
                      </Typography>
                      {(isAdminUser || canCreate) && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setOpenOcorrenciaModal(true)}
                        >
                          Nova Ocorrência
                        </Button>
                      )}
                    </Box>

                    {loadingOcorrencias ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : ocorrencias.length === 0 ? (
                      <Alert severity="info">
                        Nenhuma ocorrência registrada para este fornecedor.
                      </Alert>
                    ) : (
                      <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Título</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Prioridade</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Usuário</TableCell>
                              {isAdminUser && <TableCell sx={{ fontWeight: 700 }}>Ações</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ocorrencias.map((oc) => (
                              <TableRow key={oc.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                  {formatDataHora(oc.created_at)}
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={oc.descricao || ''} arrow>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {oc.titulo}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={oc.tipo || 'Info'}
                                    size="small"
                                    variant="outlined"
                                    color={
                                      oc.tipo === 'Problema'
                                        ? 'error'
                                        : oc.tipo === 'Urgente'
                                          ? 'warning'
                                          : 'info'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={oc.status || 'Aberta'}
                                    size="small"
                                    color={
                                      oc.status === 'Fechada'
                                        ? 'success'
                                        : oc.status === 'Em andamento'
                                          ? 'warning'
                                          : 'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>{oc.prioridade || 'Normal'}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>
                                  {oc.usuario_nome || '-'}
                                </TableCell>
                                {isAdminUser && (
                                  <TableCell>
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteOcorrencia(oc.id)}
                                    >
                                      Excluir
                                    </Button>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {detailTabIndex === 0 && !editMode && (
              <>
                {(canUpdate || isAdminUser) && (
                  <Button
                    onClick={() => {
                      setEditMode(true);
                      setCpfCnpjError('');
                      setEditForm({
                        nome: selectedFornecedor.nome,
                        nome_fantasia: selectedFornecedor.nome_fantasia || '',
                        cnpj: selectedFornecedor.cnpj || '',
                        cpf: selectedFornecedor.cpf || '',
                        telefone: selectedFornecedor.telefone || '',
                        email: selectedFornecedor.email || '',
                        cep: selectedFornecedor.cep || '',
                        logradouro: selectedFornecedor.logradouro || '',
                        numero_endereco: selectedFornecedor.numero_endereco || '',
                        complemento: selectedFornecedor.complemento || '',
                        bairro: selectedFornecedor.bairro || '',
                        cidade: selectedFornecedor.cidade || '',
                        estado: selectedFornecedor.estado || '',
                        situacao_cadastral: selectedFornecedor.situacao_cadastral || '',
                        ativo: normalizeAtivo(selectedFornecedor.ativo),
                        observacao: selectedFornecedor.observacao || '',
                        categoria: selectedFornecedor.categoria || '',
                        responsavel_nome: selectedFornecedor.responsavel_nome || '',
                        responsavel_telefone: selectedFornecedor.responsavel_telefone || '',
                      });
                    }}
                  >
                    Editar
                  </Button>
                )}
                {isAdminUser && (
                  <Button color="error" onClick={handleDelete} disabled={saving}>
                    Excluir
                  </Button>
                )}
              </>
            )}
            {detailTabIndex === 0 && editMode && (
              <>
                <Button
                  onClick={() => {
                    setEditMode(false);
                    setEditForm({});
                    setEditFile(null);
                    setCpfCnpjError('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUpdate}
                  disabled={saving || !editForm.nome}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </>
            )}
            <Button
              onClick={() => {
                setSelectedFornecedor(null);
                setEditMode(false);
                setEditForm({});
                setEditFile(null);
                setDetailTabIndex(0);
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════ */}
        {/* ─── Modal Novo/Editar Contato ─────────────────── */}
        <Dialog
          open={openContatoModal}
          onClose={() => {
            setOpenContatoModal(false);
            setEditingContato(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingContato ? 'Editar Contato' : 'Novo Contato'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nome *"
              fullWidth
              margin="normal"
              value={contatoForm.nome}
              onChange={(e) => setContatoForm((f) => ({ ...f, nome: e.target.value }))}
            />
            <TextField
              label="Cargo / Função"
              fullWidth
              margin="normal"
              value={contatoForm.cargo}
              onChange={(e) => setContatoForm((f) => ({ ...f, cargo: e.target.value }))}
            />
            <TextField
              label="Telefone"
              fullWidth
              margin="normal"
              value={contatoForm.telefone}
              onChange={(e) =>
                setContatoForm((f) => ({ ...f, telefone: formatPhone(e.target.value) }))
              }
              inputProps={{ maxLength: 15 }}
            />
            <TextField
              label="E-mail"
              fullWidth
              margin="normal"
              value={contatoForm.email}
              type="email"
              onChange={(e) => setContatoForm((f) => ({ ...f, email: e.target.value }))}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={contatoForm.principal}
                  onChange={(e) => setContatoForm((f) => ({ ...f, principal: e.target.checked }))}
                />
              }
              label="Contato principal"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenContatoModal(false);
                setEditingContato(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={saving || !contatoForm.nome}
              onClick={editingContato ? handleUpdateContato : handleCreateContato}
            >
              {saving ? 'Salvando...' : editingContato ? 'Salvar' : 'Criar Contato'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════ */}
        {/* ─── Modal de Nova Ocorrência ─────────────────── */}
        <Dialog
          open={openOcorrenciaModal}
          onClose={() => setOpenOcorrenciaModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Nova Ocorrência</DialogTitle>
          <DialogContent>
            <TextField
              label="Título *"
              fullWidth
              margin="normal"
              value={ocorrenciaForm.titulo}
              onChange={(e) => setOcorrenciaForm((f) => ({ ...f, titulo: e.target.value }))}
            />
            <TextField
              label="Descrição"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={ocorrenciaForm.descricao}
              onChange={(e) => setOcorrenciaForm((f) => ({ ...f, descricao: e.target.value }))}
            />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={ocorrenciaForm.tipo}
                    label="Tipo"
                    onChange={(e) => setOcorrenciaForm((f) => ({ ...f, tipo: e.target.value }))}
                  >
                    <MenuItem value="Informação">Informação</MenuItem>
                    <MenuItem value="Problema">Problema</MenuItem>
                    <MenuItem value="Urgente">Urgente</MenuItem>
                    <MenuItem value="Manutenção">Manutenção</MenuItem>
                    <MenuItem value="Financeiro">Financeiro</MenuItem>
                    <MenuItem value="Documentação">Documentação</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={ocorrenciaForm.status}
                    label="Status"
                    onChange={(e) => setOcorrenciaForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    <MenuItem value="Aberta">Aberta</MenuItem>
                    <MenuItem value="Em andamento">Em andamento</MenuItem>
                    <MenuItem value="Fechada">Fechada</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Prioridade</InputLabel>
                  <Select
                    value={ocorrenciaForm.prioridade}
                    label="Prioridade"
                    onChange={(e) =>
                      setOcorrenciaForm((f) => ({ ...f, prioridade: e.target.value }))
                    }
                  >
                    <MenuItem value="Baixa">Baixa</MenuItem>
                    <MenuItem value="Normal">Normal</MenuItem>
                    <MenuItem value="Alta">Alta</MenuItem>
                    <MenuItem value="Crítica">Crítica</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenOcorrenciaModal(false)}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleCreateOcorrencia}
              disabled={saving || !ocorrenciaForm.titulo}
            >
              {saving ? 'Salvando...' : 'Criar Ocorrência'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
